<template>
	<v-dialog v-model="dialog" persistent max-width="900px">
		<v-card>
			<v-toolbar flat dense card>
				<v-toolbar-title class="subheading">{{ title }}</v-toolbar-title>
				<v-spacer></v-spacer>
			</v-toolbar>

			<v-card-text class="text-xs-left">
				<v-alert v-if="isUploadingNewVersion && !certificationTypeId" :value="true" class="mb-3" type="info">
					{{ getExistingCertificationMessage }}
				</v-alert>
				<v-alert v-if="expiryWarningMessage" :value="true" type="warning" class="mb-3">{{ expiryWarningMessage }}</v-alert>

				<v-layout row>
					<v-flex sm6>
						<v-form ref="form" lazy-validation>
							<v-autocomplete v-if="!employeeId" v-model="model.employeeId" :items="employeeSelectItems" placeholder="Select Employee" />

							<v-select v-if="!certificationTypeId" v-model="model.certificationTypeId" :items="certTypeSelectItems" placeholder="Select Certification Type" item-value="id" item-text="name" class="certification-type-select">
								<template slot="item" slot-scope="data">
									<v-list-tile-content>
										<v-list-tile-title class="font-weight-bold">{{ data.item.name }}</v-list-tile-title>
										<v-list-tile-sub-title class="text--secondary caption">
											{{ $_snipText(data.item.description, 75) }}
										</v-list-tile-sub-title>
									</v-list-tile-content>
								</template>
								<template slot="selection" slot-scope="data">
									{{ data.item.name }}
								</template>
							</v-select>
							<DateInput v-if="showExpiryField" v-model="model.expiryDate" label="Expiry Date" />
						</v-form>
					</v-flex>
					<v-flex sm6>
						<v-subheader class="my-3">
							Please upload one or more files that include a clear image or PDF of the certificate. Supporting documents are also welcome if relevant.
						</v-subheader>
						<file-upload-base class="text-xs-center" ref="fileUploader" hide-title flat :loading="loading" multiple @onFileChange="handleFileChange" />
					</v-flex>
				</v-layout>
			</v-card-text>

			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn flat @click="close" :disabled="isSaving">Cancel</v-btn>
				<v-btn color="primary" flat @click="save" :disabled="isSaveBtnDisabled || isSaving" :loading="isSaving">Save</v-btn>
			</v-card-actions>
		</v-card>

		<confirmation-dialog
			:dialog.sync="showSaveWithoutExpiryDialog"
			title="Missing Expiry"
			text="This certification type normally has an expiry date, but you haven't entered one. Are you sure you want to continue?"
			confirmButtonText="Continue Without Expiry"
			cancelButtonText="Go Back"
			@onConfirm="confirmSaveWithoutExpiry"
			@onCancel="showSaveWithoutExpiryDialog = false"
		/>

		<confirmation-dialog
			:dialog.sync="showSaveWithoutFilesDialog"
			title="No Files Uploaded"
			text="You haven’t uploaded any files. Are you sure you want to continue without uploading a certification?"
			confirmButtonText="Continue Without Files"
			cancelButtonText="Go Back"
			@onConfirm="confirmSaveWithoutFiles"
			@onCancel="showSaveWithoutFilesDialog = false"
		/>
	</v-dialog>
</template>

<script>
	import FileUploadBase from '@components/FileUploadBase.vue';
	import { mapActions, mapGetters } from 'vuex';
	import DateInput from '@components/common/DateInput.vue';
	import moment from 'moment';
	import textSnipMixin from '@/mixins/textSnipMixin';

	export default {
		name: 'CertificationUploader',
		components: { DateInput, FileUploadBase },
		mixins: [textSnipMixin],
		props: {
			value: Boolean,
			employeeId: {
				type: String,
				default: null
			},
			certificationTypeId: {
				type: String,
				default: null
			}
		},
		data() {
			return {
				loading: false,
				dialog: false,
				model: this.getEmptyModel(),
				expiryDate: null,
				isSaving: false,
				showSaveWithoutExpiryDialog: false,
				showSaveWithoutFilesDialog: false
			};
		},
		computed: {
			...mapGetters({
				certificationTypes: 'certificationTypes/getTypes',
				activeEmployees: 'employees/employeesMiniActiveOnly',
				currentEmployeeUnderlings: 'employees/myUnderlings',
				certificationsByEmployee: 'certifications/certificationsByEmployee',
				isAdmin: 'loggedInEmployeeIsAdmin'
			}),
			isSaveBtnDisabled() {
				const formNotFilled = !!!this.model.certificationTypeId || !!!this.model.employeeId || (this.isUploadingNewVersion && this.model.files.length === 0)

				return formNotFilled;
			},
			certTypeSelectItems() {
				return this.certificationTypes.map(x => ({
					id: x.id,
					name: x.name,
					description: x.description
				}));
			},
			employeeSelectItems() {
				if (this.isAdmin) {
					return this.activeEmployees.map(x => {
						return { text: x.fullName, value: x.id };
					});
				}
				return this.currentEmployeeUnderlings.map(x => {
					return { text: x.fullName, value: x.id };
				});
			},
			selectedCertificationType() {
				if (!this.model.certificationTypeId) return;

				return this.certificationTypes.find(x => x.id === this.model.certificationTypeId);
			},
			showExpiryField() {
				if (!this.selectedCertificationType) return false;
				return this.selectedCertificationType.configuration.hasExpiry;
			},
			existingCertification() {
				if (!this.model.employeeId || !this.model.certificationTypeId) return null;
				return this.certificationsByEmployee(this.model.employeeId).find(x => x.certificationTypeId === this.model.certificationTypeId && !x.deleted);
			},
			isUploadingNewVersion() {
				if (!this.existingCertification) return false;
				return !!this.existingCertification;
			},
			expiryWarningMessage() {
				if (moment(this.model.expiryDate).isBefore(moment())) return 'Warning: Expiry date is in the past';

				if (this.existingCertification && moment(this.model.expiryDate).isBefore(moment(this.existingCertification.expiryDate))) {
					return 'Warning: New expiry date is before the existing certification expiry date';
				}

				if (this.existingCertification && moment(this.model.expiryDate).isBefore(moment(this.existingCertification.expiryDate).add(30, 'days'))) {
					return 'Warning: New expiry date is within 30 days of the existing certification expiry date';
				}

				return '';
			},
			getExistingCertificationMessage() {
				if (!this.existingCertification) return '';
				return `${this.getEmployeeName(this.model.employeeId)} already has a ${this.existingCertification.name}. This will update their existing certification.`;
			},
			title() {
				return this.isUploadingNewVersion ? `Update ${this.existingCertification.name}` : 'Add Certification';
			}
		},
		watch: {
			value: {
				handler(val) {
					this.dialog = val;
					if (val) {
						this.$refs.fileUploader.handleFileDelete();
						this.model = this.getEmptyModel();
					}
				},
				immediate: true
			},
			dialog(val) {
				this.$emit('input', val);
			},
			employeeId(val) {
				this.model.employeeId = val;
			},
			certificationTypeId(val) {
				this.model.certificationTypeId = val;
			}
		},
		methods: {
			...mapActions({
				uploadCertification: 'certifications/uploadCertification'
			}),
			getEmptyModel() {
				return {
					employeeId: this.employeeId || '',
					expiryDate: '',
					certificationTypeId: this.certificationTypeId || '',
					files: []
				};
			},
			handleFileChange(files) {
				this.model.files = files;
			},
			close() {
				this.dialog = false;
			},
			save() {
				const isValid = this.$refs.form.validate();
				if (!isValid) return;

				const requiresExpiry = this.selectedCertificationType.configuration.hasExpiry;
				const noExpiryEntered = !this.model.expiryDate;

				if (requiresExpiry && noExpiryEntered) {
					this.showSaveWithoutExpiryDialog = true;
					return;
				}

				const noFilesUploaded = !this.model.files || this.model.files.length === 0;
				if (noFilesUploaded) {
					this.showSaveWithoutFilesDialog = true;
					return;
				}

				this.continueSave();
			},
			async continueSave() {
				const isValid = this.$refs.form.validate();
				if (!isValid) return;

				this.isSaving = true;

				const formData = new FormData();

				for (const file of this.model.files) {
					formData.append('files', file);
				}

				formData.append('employeeId', this.model.employeeId);
				formData.append('expiryDate', this.model.expiryDate);
				formData.append('certificationTypeId', this.model.certificationTypeId);

				const result = await this.uploadCertification(formData);

				if (result.isSuccess) {
					this.$emit('onCertificationAdded', result.data);
					this.close();
				}

				this.isSaving = false;
			},
			getEmployeeName(employeeId) {
				return this.employeeSelectItems.find(x => x.value === employeeId).text;
			},
			confirmSaveWithoutExpiry() {
				this.showSaveWithoutExpiryDialog = false;
				this.continueSave();
			},
			confirmSaveWithoutFiles() {
				this.showSaveWithoutFilesDialog = false;
				this.continueSave();
			}
		}
	};
</script>

<style scoped></style>
